import { useState } from "react";

import {
  IAdminPatientDetail,
  IPatientCondition,
} from "../../../../@types/admin/AdminPatient";
import { useChangePatientCondition } from "../../../../hooks/mutations/admin/patients";
import { useConditionTypeOptions } from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";

interface IChangeConditionModalProps {
  patient: IAdminPatientDetail;
  /** The patient's current published condition, when they have one. */
  currentCondition: IPatientCondition | undefined;
  onDone: () => void;
}

/**
 * Changes the patient's condition (EN-5169) — Retool's `modalChangeCondition`.
 *
 * ⚠️ The most clinically significant write on this page. The condition decides
 * which measurement cards the patient app shows them and which workflows
 * evaluate for them, so changing it can both hide a measurement they were
 * recording and start or stop monitoring. Retool submitted this straight from
 * the form with no confirmation of any kind — the warning here is the
 * confirmation.
 */
export function ChangeConditionModal({
  patient,
  currentCondition,
  onDone,
}: IChangeConditionModalProps) {
  const { showToast } = useToast();
  const { conditionTypes } = useConditionTypeOptions();
  const { changeCondition, isChangingCondition } = useChangePatientCondition();

  const [conditionTypeId, setConditionTypeId] = useState("");

  const selected = conditionTypes.find(
    (type) => String(type.id) === conditionTypeId
  );
  // Changing to the condition they already have is a no-op write; the API would
  // accept it and the audit trail would record a change that did nothing.
  const isSameAsCurrent =
    currentCondition?.typeId !== undefined &&
    currentCondition.typeId !== null &&
    String(currentCondition.typeId) === conditionTypeId;

  const handleSubmit = async () => {
    if (!conditionTypeId || isSameAsCurrent) return;

    try {
      await changeCondition({
        patientId: Number(patient.id),
        conditionTypeId: Number(conditionTypeId),
      });
      showToast("The condition was changed successfully.", "success");
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error changing the condition."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Change condition</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Current condition:{" "}
          <strong>{currentCondition?.name || "none recorded"}</strong>
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="new-condition">New condition</FilterLabel>
          <AdminSelectField
            id="new-condition"
            value={conditionTypeId}
            onChange={(e) => setConditionTypeId(e.target.value)}
          >
            <option value="">Select a condition</option>
            {conditionTypes.map((type) => (
              <option key={type.id} value={String(type.id)}>
                {type.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        {isSameAsCurrent && (
          <ModalBodyText>
            That is already this patient&apos;s condition.
          </ModalBodyText>
        )}

        {selected && !isSameAsCurrent && (
          <WarningNotice>
            Moving this patient to <strong>{selected.name}</strong> changes
            which measurements their app asks for and which workflows evaluate
            for them. Monitoring that depends on the current condition will
            stop.
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isChangingCondition}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={
              isChangingCondition || !conditionTypeId || isSameAsCurrent
            }
            onClick={handleSubmit}
          >
            {isChangingCondition ? "Changing..." : "Change condition"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
